import { ref, computed, readonly, onMounted, onUnmounted } from 'vue'
import { getCurrentPosition } from '@/services/geolocation/geolocation'
import { validateLocation } from '@/services/geolocation/geofence'
import { useSettingsStore } from '@/stores/settings'
import { useAdminStore } from '@/stores/admin'
import type { LocationStatus } from '@/components/common/LocationStatusCard.vue'
import type { PositionResult, GeolocationError } from '@/types/geofence'

// Shared reactive state - this will be the same instance across all components
const isActive = ref(false)
const isLoading = ref(false)
const status = ref<LocationStatus>('loading')
const error = ref<string | null>(null)
const lastCheckTime = ref<Date | null>(null)
const distance = ref<number | null>(null)
const currentPosition = ref<any>(null)

// Position watcher reference
let positionWatcher: any = null

// Store integration
let settingsStore: any = null
let adminStore: any = null

// Track if position watching has been initialized
let isInitialized = false

// Mutex to prevent concurrent location updates (prevents race conditions)
let isUpdating = false

// Watchdog timer to prevent stuck state
let updateWatchdog: NodeJS.Timeout | null = null
const UPDATE_TIMEOUT_MS = 35000 // 35 seconds max for any update (accounts for 15s + 10s geolocation attempts + buffer)

// Computed properties
const isInsideGeofence = computed(() => status.value === 'inside')
const hasError = computed(() => error.value !== null)
const canRefresh = computed(() => !isLoading.value)

/**
 * Start watchdog timer to prevent stuck state
 */
function startWatchdog(): void {
    // Clear any existing watchdog
    if (updateWatchdog) {
        clearTimeout(updateWatchdog)
    }

    // Set new watchdog
    updateWatchdog = setTimeout(() => {
        console.error('[useSharedGeolocation] Update watchdog triggered - resetting stuck state')
        isUpdating = false
        isLoading.value = false
        updateWatchdog = null
    }, UPDATE_TIMEOUT_MS)
}

/**
 * Stop watchdog timer
 */
function stopWatchdog(): void {
    if (updateWatchdog) {
        clearTimeout(updateWatchdog)
        updateWatchdog = null
    }
}

/**
 * Calculate distance to geofence area (simplified approach)
 */
async function calculateDistanceToArea(): Promise<number | null> {
    if (!currentPosition.value || !settingsStore) return null

    try {
        const geofence = settingsStore.geofence
        if (!geofence || !geofence.points || geofence.points.length < 3) return null

        // Initialize admin store if needed
        if (!adminStore) {
            adminStore = useAdminStore()
            // Try to load debug settings if not already loaded
            if (!adminStore.debugSettings.lastModified) {
                await adminStore.loadDebugSettings()
            }
        }

        // Use existing geofence validation to check if inside
        const validation = validateLocation(
            { lat: currentPosition.value.lat, lng: currentPosition.value.lng },
            currentPosition.value.accuracy || 50,
            geofence.points,
            adminStore.debugSettings.bypassGeofence
        )

        if (validation.isInsideGeofence) {
            return 0
        } else {
            // For outside, calculate approximate distance to center of geofence
            const centerLat =
                geofence.points.reduce((sum: number, p: any) => sum + p.lat, 0) /
                geofence.points.length
            const centerLng =
                geofence.points.reduce((sum: number, p: any) => sum + p.lng, 0) /
                geofence.points.length

            // Simple distance calculation (not precise but good enough for display)
            const latDiff = currentPosition.value.lat - centerLat
            const lngDiff = currentPosition.value.lng - centerLng
            const distance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff) * 111000 // Rough conversion to meters

            return Math.round(distance)
        }
    } catch (err) {
        console.error('[useSharedGeolocation] Distance calculation failed:', err)
        return null
    }
}

/**
 * Unified function to update location state
 * This is the single source of truth for location validation
 * Called by both manual refresh and position watcher
 */
async function updateLocationState(position: PositionResult): Promise<void> {
    // Update current position first
    currentPosition.value = {
        lat: position.coords.lat,
        lng: position.coords.lng,
        accuracy: position.accuracy
    }

    // Initialize settings store if needed (defensive check)
    if (!settingsStore) {
        settingsStore = useSettingsStore()
    }

    // Get geofence config
    const geofence = settingsStore.geofence
    if (!geofence || !geofence.points || geofence.points.length < 3) {
        // Config not available - don't mark as error, just wait
        console.warn('[useSharedGeolocation] Geofence config not available for validation')
        return
    }

    // Initialize admin store to check debug settings
    if (!adminStore) {
        adminStore = useAdminStore()
        // Try to load debug settings if not already loaded
        if (!adminStore.debugSettings.lastModified) {
            try {
                await adminStore.loadDebugSettings()
            } catch (err) {
                console.error('[useSharedGeolocation] Failed to load debug settings:', err)
            }
        }
    }

    // Validate location against geofence with debug bypass check
    const validation = validateLocation(
        position.coords,
        position.accuracy,
        geofence.points,
        adminStore.debugSettings.bypassGeofence
    )

    // Update status based on validation result
    if (!validation.isAccurate) {
        status.value = 'poor-accuracy'
        error.value = validation.message
    } else if (!validation.isInsideGeofence) {
        status.value = 'outside'
        error.value = null
    } else {
        status.value = 'inside'
        error.value = null
    }

    // Calculate distance (best effort - don't fail if this errors)
    try {
        const distanceToArea = await calculateDistanceToArea()
        distance.value = distanceToArea
    } catch (err) {
        console.error('[useSharedGeolocation] Distance calculation failed:', err)
        // Keep existing distance value, don't update status
    }

    lastCheckTime.value = new Date()
}

/**
 * Check current location and update status
 */
async function checkLocation(): Promise<void> {
    // Prevent concurrent updates
    if (isLoading.value || isUpdating) return

    isUpdating = true
    isLoading.value = true
    error.value = null
    status.value = 'loading'

    // Start watchdog to prevent stuck state
    startWatchdog()

    try {
        // Initialize settings store if needed
        if (!settingsStore) {
            settingsStore = useSettingsStore()
        }

        // Check if geofence configuration is available before getting position
        // On fresh setup, config might not be loaded yet
        const geofence = settingsStore.geofence
        if (!geofence || !geofence.points || geofence.points.length < 3) {
            // Try to load config if not available
            if (!settingsStore.isLoaded) {
                await settingsStore.loadCachedConfig()
                await settingsStore.loadConfig()
            }

            // Check again after loading
            const geofenceAfterLoad = settingsStore.geofence
            if (!geofenceAfterLoad || !geofenceAfterLoad.points || geofenceAfterLoad.points.length < 3) {
                error.value = 'Geofence configuration not available'
                status.value = 'config-missing'
                lastCheckTime.value = new Date()
                return
            }
        }

        // Initialize admin store to check debug settings
        if (!adminStore) {
            adminStore = useAdminStore()
            // Try to load debug settings if not already loaded
            if (!adminStore.debugSettings.lastModified) {
                try {
                    await adminStore.loadDebugSettings()
                } catch (err) {
                    console.error('[useSharedGeolocation] Failed to load debug settings:', err)
                }
            }
        }

        // CHECK BYPASS BEFORE ATTEMPTING GPS LOCATION
        if (adminStore.debugSettings.bypassGeofence) {
            console.log('[useSharedGeolocation] Geofence bypass enabled - skipping location check')
            status.value = 'inside'
            error.value = null
            distance.value = 0
            lastCheckTime.value = new Date()
            return
        }

        // Get current position
        const positionResult = await getCurrentPosition()

        // Handle geolocation errors
        if ('error' in positionResult) {
            const geoError = positionResult as GeolocationError
            error.value = geoError.message

            switch (geoError.error) {
                case 'PERMISSION_DENIED':
                    status.value = 'permission-denied'
                    break
                case 'LOCATION_UNAVAILABLE':
                    status.value = 'unavailable'
                    break
                case 'TIMEOUT':
                    status.value = 'unavailable'
                    break
                default:
                    status.value = 'unavailable'
            }

            lastCheckTime.value = new Date()
            return
        }

        // Validate position result and update state using unified function
        const position = positionResult as PositionResult
        await updateLocationState(position)
    } catch (err) {
        console.error('[useSharedGeolocation] Location check failed:', err)
        error.value = 'Failed to check location. Please try again.'
        status.value = 'unavailable'
        lastCheckTime.value = new Date()
    } finally {
        stopWatchdog()
        isLoading.value = false
        isUpdating = false
    }
}

/**
 * Manual refresh of location
 */
async function refreshLocation(): Promise<void> {
    await checkLocation()
}

/**
 * Start position watching (real-time GPS updates)
 */
async function startWatching(): Promise<void> {
    if (positionWatcher) return

    try {
        const { Geolocation } = await import('@capacitor/geolocation')

        positionWatcher = await Geolocation.watchPosition(
            {
                enableHighAccuracy: true,
                maximumAge: 0,
                timeout: 10000
            },
            (position, err) => {
                if (err) {
                    return
                }

                if (position && isActive.value) {
                    // Process the position update
                    processPositionUpdate(position)
                }
            }
        )
    } catch (error) {
        console.error('[useSharedGeolocation] Failed to start position watching:', error)

        // Fallback to polling if watchPosition fails
        startPollingFallback()
    }
}

/**
 * Fallback to polling system if watchPosition fails
 */
function startPollingFallback(): void {
    const pollInterval = setInterval(() => {
        if (isActive.value) {
            checkLocation()
        }
    }, 30000) // 30 seconds

    // Store the interval ID for cleanup
    positionWatcher = pollInterval
}

/**
 * Stop position watching
 */
async function stopWatching(): Promise<void> {
    if (positionWatcher) {
        try {
            // Check if it's a polling interval (number) or watchPosition ID
            if (typeof positionWatcher === 'number') {
                // It's a polling interval
                clearInterval(positionWatcher)
            } else {
                // It's a watchPosition ID
                const { Geolocation } = await import('@capacitor/geolocation')
                await Geolocation.clearWatch({ id: positionWatcher })
            }
            positionWatcher = null
        } catch (error) {
            console.error('[useSharedGeolocation] Failed to stop position watching:', error)
        }
    }
}

/**
 * Process position update from watcher
 * Uses unified updateLocationState for consistency
 */
async function processPositionUpdate(capacitorPosition: any): Promise<void> {
    // Skip if concurrent update in progress (prevents race conditions)
    if (!capacitorPosition || isUpdating) return

    // Initialize admin store if needed
    if (!adminStore) {
        adminStore = useAdminStore()
    }

    // Skip location processing if bypass is enabled
    if (adminStore.debugSettings.bypassGeofence) {
        console.log('[useSharedGeolocation] Geofence bypass enabled - skipping position update')
        return
    }

    isUpdating = true

    // Start watchdog to prevent stuck state
    startWatchdog()

    try {
        // Convert Capacitor position format to our PositionResult format
        const position: PositionResult = {
            coords: {
                lat: capacitorPosition.coords.latitude,
                lng: capacitorPosition.coords.longitude
            },
            accuracy: capacitorPosition.coords.accuracy || 50,
            timestamp: capacitorPosition.timestamp || Date.now()
        }

        // Use unified state update function
        await updateLocationState(position)
    } catch (err) {
        console.error('[useSharedGeolocation] Position update failed:', err)
        // Don't update status on error - keep current state
    } finally {
        stopWatchdog()
        isUpdating = false
    }
}

/**
 * Activate location tracking (start position watching) - following your working pattern
 */
async function activate(): Promise<void> {
    isActive.value = true

    // Initialize settings store
    if (!settingsStore) {
        settingsStore = useSettingsStore()
    }

    // Initialize admin store to check debug settings
    if (!adminStore) {
        adminStore = useAdminStore()
        // Try to load debug settings if not already loaded
        if (!adminStore.debugSettings.lastModified) {
            try {
                await adminStore.loadDebugSettings()
            } catch (err) {
                console.error('[useSharedGeolocation] Failed to load debug settings:', err)
            }
        }
    }

    // Perform initial location check immediately
    await checkLocation()

    // Start position watching only if bypass is NOT enabled
    if (!adminStore.debugSettings.bypassGeofence) {
        await startWatching()
    } else {
        console.log('[useSharedGeolocation] Geofence bypass enabled - not starting position watcher')
    }
}

/**
 * Deactivate location tracking (stop position watching)
 */
async function deactivate(): Promise<void> {
    isActive.value = false
    await stopWatching()
}

/**
 * Reset all state
 */
async function reset(): Promise<void> {
    await deactivate()
    status.value = 'loading'
    error.value = null
    lastCheckTime.value = null
    distance.value = null
    currentPosition.value = null
    isLoading.value = false
    isUpdating = false // Reset mutex to prevent stuck state
}

/**
 * Shared geolocation composable - all components get the same reactive state
 */
export function useSharedGeolocation() {
    // Lifecycle management for the shared state - only initialize once
    onMounted(() => {
        if (!import.meta.env.VITEST && !isInitialized) {
            isInitialized = true
            activate()
        }
    })

    onUnmounted(() => {
        // Only deactivate if this is the last component using it
        // For now, we'll keep it simple and not track component count
        // deactivate()
    })

    return {
        // State - return the actual refs for reactivity
        isActive: readonly(isActive),
        isLoading: readonly(isLoading),
        status: readonly(status),
        error: readonly(error),
        lastCheckTime: readonly(lastCheckTime),
        distance: readonly(distance),
        currentPosition: readonly(currentPosition),

        // Computed
        isInsideGeofence,
        hasError,
        canRefresh,

        // Methods
        checkLocation,
        refreshLocation,
        activate,
        deactivate,
        reset
    }
}
